import { useState, useEffect } from "react";
import { styled } from "@mui/material/styles";
// mui components
import PropTypes from "prop-types";

import Stack from "@mui/material/Stack";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";

import TableChartIcon from "@mui/icons-material/TableChart";
import EqualizerIcon from "@mui/icons-material/Equalizer";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import { useSelector } from "react-redux";
// redux

import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";

const LightTooltip = styled(({ className, ...props }) => (
    <Tooltip
        {...props}
        classes={{ popper: className }}
    />
))(({ theme }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
        backgroundColor: theme.palette.common.white,
        color: "rgba(0, 0, 0, 0.87)",
        //   boxShadow: theme.shadows[1],
        fontSize: 11,
    },
}));

function GraphLayoutHeader({
    title = "",
    backendVariable = "",
    fetchSwitchState = () => {},
    sampleSize = 0,
    // fetchModalState = () => {},
}) {
    const switchState = useSelector((state) => state.respondentDashboardStore.switchState);

    const [sampleSizeDetails, setSampleSizeDetails] = useState({});

    const handleChange = (event, newVal, title) => {
        if (newVal) {
            fetchSwitchState(title, newVal);
        }
    };

    const children = [
        <ToggleButton
            value="countGraph"
            key="countGraph"
        >
            <Tooltip
                title="Counts"
                placement="top"
            >
                <EqualizerIcon
                    sx={{
                        fontSize: "16px",
                    }}
                />
            </Tooltip>
        </ToggleButton>,
        <ToggleButton
            value="statsTable"
            key="statsTable"
        >
            <Tooltip
                title="Stats"
                placement="top"
            >
                <TableChartIcon
                    sx={{
                        fontSize: "16px",
                    }}
                />
            </Tooltip>
        </ToggleButton>,
    ];

    const getSampleSizeColor = (sampleSize) => {
        if (sampleSize <= 30) return { color: "#FF0000", message: "Sample size is below 30" };
        if (sampleSize > 30 && sampleSize <= 50) return { color: "#ffa500", message: "The sample size falls between 31 and 50" };
        return { color: "#000000" };
    };

    useEffect(() => {
        if (sampleSize > 0) {
            const tempDetails = getSampleSizeColor(sampleSize);
            setSampleSizeDetails(tempDetails);
        }
    }, [sampleSize]);

    return (
        <>
            <style>
                {`
                    .clearfix::after {
                        content: "";
                        clear: both;
                        display: table;
                    }
                `}
            </style>
            <div className="clearfix">
                <span
                    style={{
                        fontSize: "12px",
                        fontWeight: "bold",
                        display: "inline",
                        position: "relative",
                        float: "left",
                    }}
                >
                    <span style={{ color: sampleSizeDetails.color }}>(n={sampleSize})</span>

                    {sampleSize <= 50 ? (
                        <LightTooltip
                            title={sampleSizeDetails.message || `Sample size is ${sampleSize}`}
                            placement="right"
                        >
                            <InfoOutlinedIcon
                                style={{
                                    fontSize: "11px",
                                    color: sampleSizeDetails.color,
                                    cursor: "pointer",
                                    marginTop: "-8px",
                                }}
                            />
                        </LightTooltip>
                    ) : null}
                </span>
                <h6
                    className="text-sm color-blue"
                    style={{
                        marginTop: "8px",
                        display: "inline",
                        position: "relative",
                    }}
                >
                    {title}
                </h6>
                <div
                    className="clearfix"
                    style={{
                        color: "#005eef",
                        display: "inline-block",
                        float: "right",
                    }}
                >
                    <Stack
                        spacing={2}
                        alignItems="center"
                        style={{
                            marginBottom: "10px",
                        }}
                    >
                        <ToggleButtonGroup
                            size="small"
                            value={switchState[backendVariable] || "countGraph"}
                            onChange={(event, value) => handleChange(event, value, backendVariable)}
                            exclusive={true}
                            color="primary"
                            aria-label="Small sizes"
                        >
                            {children}
                        </ToggleButtonGroup>
                    </Stack>
                </div>
            </div>
            <hr
                style={{
                    margin: 0,
                }}
            />
        </>
    );
}

// Typechecking props for the GraphLayout
GraphLayoutHeader.propTypes = {
    title: PropTypes.string,
    backendVariable: PropTypes.string,
    fetchSwitchState: PropTypes.func,
    sampleSize: PropTypes.number,
};

export default GraphLayoutHeader;
